import { useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import Modal from '../../common/Modal';
import ModalAction from '../../common/ModalAction';
import VideoTimeline from '../../common/VideoTimeline';
import useYouTubePlayer from '../../../hooks/useYouTubePlayer';
import useSpaceToPlay from '../../../hooks/useSpaceToPlay';
import { previousStep, watchVideosUrl } from '../../../utils/youtubePlaylist';
import { ReactComponent as PlayIcon } from '../../../icons/play-solid.svg';
import { ReactComponent as PauseIcon } from '../../../icons/pause-solid.svg';
import { ReactComponent as PreviousIcon } from '../../../icons/skip-previous.svg';
import { ReactComponent as NextIcon } from '../../../icons/skip-next.svg';
import { ReactComponent as YouTubeIcon } from '../../../icons/youtube.svg';
import { ReactComponent as SoundIcon } from '../../../icons/sound-high-solid.svg';

import './PlaylistModal.scss';

/** A service's songs played one after another in the app's own player, with YouTube's playlist a tap away */
const PlaylistModal = ({ tracks, onClose }) => {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  // The player is made once, for the first song; every song after is loaded into it
  const [firstVideoId] = useState(tracks[0]?.videoId);

  const goTo = (next) => {
    setIndex(next);
    load(tracks[next].videoId);
  };

  const { mountRef, ready, failed, playing, time, duration, play, pause, seek, load, currentTime } = useYouTubePlayer(
    firstVideoId,
    {
      onEnded: () => {
        if (index < tracks.length - 1) goTo(index + 1);
      },
    },
  );

  const togglePlay = () => (playing ? pause() : play());

  const handlePrevious = () => {
    const step = previousStep(index, currentTime());
    if (step.restart) seek(0);
    else goTo(step.index);
  };

  useSpaceToPlay(ready ? togglePlay : null);

  const isLast = index === tracks.length - 1;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t('dialogs.playlist.title')}
      className="PlaylistModal"
      actions={
        // YouTube's own playlist of the same songs; pauses here, so the two don't play over each other
        <ModalAction
          icon={<YouTubeIcon />}
          label={t('dialogs.playlist.openInYouTube')}
          href={watchVideosUrl(tracks.map((track) => track.videoId))}
          onClick={pause}
          showLabel
        />
      }
    >
      <div className="columns">
        <div className="stage">
          <div className="player-frame">
            <div className="player" ref={mountRef} />
            {!ready && (
              <div className="player-status">
                {failed ? t('dialogs.playlist.failed') : t('dialogs.playlist.loading')}
              </div>
            )}
          </div>

          <VideoTimeline time={time} duration={duration} onSeek={seek} label={t('dialogs.playlist.seek')} />

          <div className="transport">
            <button
              type="button"
              className="skip"
              aria-label={t('dialogs.playlist.previous')}
              onClick={handlePrevious}
              disabled={!ready}
            >
              <PreviousIcon />
            </button>
            <button
              type="button"
              className="play-pause"
              aria-label={t(`dialogs.playlist.${playing ? 'pause' : 'play'}`)}
              onClick={togglePlay}
              disabled={!ready}
            >
              {playing ? <PauseIcon /> : <PlayIcon />}
            </button>
            <button
              type="button"
              className="skip"
              aria-label={t('dialogs.playlist.next')}
              onClick={() => goTo(index + 1)}
              disabled={!ready || isLast}
            >
              <NextIcon />
            </button>
          </div>
        </div>

        <ol className="tracks">
          {tracks.map((track, i) => (
            <li key={track.id}>
              <button
                type="button"
                className={cx('track', { 'is-current': i === index })}
                onClick={() => (i === index ? togglePlay() : goTo(i))}
                disabled={!ready}
                aria-current={i === index ? 'true' : undefined}
              >
                <span className="number">
                  {i === index && playing ? <SoundIcon aria-label={t('dialogs.playlist.playing')} /> : i + 1}
                </span>
                <span className="names">
                  <span className="song-title">{track.title}</span>
                  {track.artist && <span className="artist">{track.artist}</span>}
                </span>
              </button>
            </li>
          ))}
        </ol>
      </div>
    </Modal>
  );
};

PlaylistModal.propTypes = {
  // From playlistTracks: the service's songs that have a video, in order
  tracks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      artist: PropTypes.string,
      videoId: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default PlaylistModal;
